import { Box, Group, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import type { UserNotificationDto } from "@shared/notifications";
import {
  formatRelativeTime,
  getNotificationIconConfig,
  NOTIFICATION_BORDER_COLOR,
  NOTIFICATION_READ_BG,
  NOTIFICATION_UNREAD_BG,
  NOTIFICATION_UNREAD_DOT,
} from "@/components/notifications/notificationDisplay.util";

function UnreadDot() {
  return (
    <Box
      w={8}
      h={8}
      style={{
        borderRadius: "50%",
        backgroundColor: NOTIFICATION_UNREAD_DOT,
        flexShrink: 0,
        alignSelf: "center",
      }}
    />
  );
}

type NotificationInboxRowProps = {
  item: UserNotificationDto;
  onClick: () => void;
  showChevron?: boolean;
};

export default function NotificationInboxRow({
  item,
  onClick,
  showChevron = false,
}: NotificationInboxRowProps) {
  const isUnread = !item.readAt;
  const { icon: TypeIcon, iconColor, circleBg } = getNotificationIconConfig(item.type);
  const canNavigate = Boolean(item.actionUrl);

  return (
    <UnstyledButton
      w="100%"
      onClick={onClick}
      style={{
        display: "block",
        borderBottom: `1px solid ${NOTIFICATION_BORDER_COLOR}`,
        backgroundColor: isUnread ? NOTIFICATION_UNREAD_BG : NOTIFICATION_READ_BG,
      }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start" py="md" pl="md" pr={isUnread || showChevron ? "sm" : "md"}>
        <ThemeIcon size={40} radius="xl" style={{ backgroundColor: circleBg, flexShrink: 0 }}>
          <TypeIcon size={20} stroke={1.75} color={iconColor} />
        </ThemeIcon>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
            <Text size="sm" fw={700} c="dark.7" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
              {item.title}
            </Text>
            <Text size="xs" c="dimmed" style={{ flexShrink: 0, lineHeight: 1.4 }}>
              {formatRelativeTime(item.createdAt)}
            </Text>
          </Group>
          <Text size="sm" c="dimmed" mt={4} lh={1.45} lineClamp={3}>
            {item.body}
          </Text>
        </Box>
        {isUnread ? <UnreadDot /> : null}
        {showChevron && canNavigate ? (
          <IconChevronRight
            size={18}
            stroke={1.75}
            color="var(--mantine-color-gray-5)"
            style={{ flexShrink: 0, alignSelf: "center" }}
          />
        ) : null}
      </Group>
    </UnstyledButton>
  );
}
